import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useNavigationContext } from "../hooks/useNavContext";
import {
    NavigationSectionConfig,
    SectionContext,
} from "../types/Navigation.types";

export interface ContextMenuProps {
    title?: string;
    navigationConfig: NavigationSectionConfig[];
    onValueChange?: (value: SectionContext) => void;
    className?: string;
}

export function ContextMenu({
    title,
    navigationConfig,
    onValueChange,
    className,
}: ContextMenuProps) {
    const { sections } = useNavigationContext(navigationConfig);
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const selectedOption = sections.find((section) => section.isCurrent);
    const displayLabel = selectedOption?.label || "-";

    const handleOptionSelect = (section: SectionContext) => {
        const url = section.href;
        if (url.startsWith("http") || url.includes("://")) {
            window.location.href = url;
        } else {
            navigate(url);
        }
        onValueChange?.(section);
        setIsOpen(false);
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="flex shrink-0 flex-col gap-2">
            {title && <p className="pageheading text-default">{title}</p>}
            {selectedOption && (
                <div className="relative">
                    <div
                        ref={buttonRef}
                        onClick={toggleMenu}
                        className={clsx(
                            "flex cursor-pointer items-center gap-1",
                            "heading-s subdued transition-colors hover:text-gray-300",
                            className,
                        )}
                    >
                        <span>{displayLabel}</span>
                        <ChevronDownIcon
                            className={clsx(
                                "size-3 transition-transform duration-200",
                                isOpen && "rotate-180",
                            )}
                        />
                    </div>

                    {isOpen && (
                        <div
                            ref={menuRef}
                            className={clsx(
                                "bg-slate-800",
                                "rounded-lg shadow-400",
                                "custom-scrollbar max-h-[400px] overflow-auto p-2",
                                "absolute left-0 top-full z-50 mt-1",
                                "min-w-[160px]",
                                "flex flex-col gap-1",
                            )}
                        >
                            {sections.map((section) => (
                                <button
                                    key={section.label}
                                    type="button"
                                    onClick={() => handleOptionSelect(section)}
                                    className={clsx(
                                        "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                        "menu-item hover:bg-slate-600",
                                        section.isCurrent &&
                                            "bg-gray-100 font-medium dark:bg-slate-700",
                                    )}
                                    role="menuitem"
                                >
                                    <span className="heading-s">
                                        {section.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
